import React, { useRef, useEffect, useState } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../actions";
import CommonContainerBox from "../custom/containers/CommonContainerBox";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import CardList from "../custom/containers/cardList";
import ErrorMessage from "../custom/containers/ErrorMessage";
import { transactionSearchHeading } from "../../constants/TransactionSearchConstants.js";
import { RoleType }  from "../../constants/sharedConstants.js";
import { useLocation } from "react-router-dom";
import { listProps, setFinancialInstitutionUIData } from "../../utils/helper.js";
import { ResponsiveBox, Typography } from "../primitives";
import { useMainContentFocus } from "../../hooks";


const TraceQueue = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        history,
        location: { state },
    } = props;

    const [loading, setLoading] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [rowPerPage, setRowPerPage] = useState(100);
    const [dataCount, setDataCount] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);
    const location = useLocation();

    const user = useSelector((state) => state.loginUserReducer.user);
    const [isBillerRole] = useState(user.role.role_type === RoleType.BILLER_ADMIN.type || user.role.role_type === RoleType.BILLER_USER.type);
    const currentFiNumber = useSelector((state) => state.fiReducer?.selectedFi || user?.fiNumber);

    const tqResponse = useSelector(
        (state) => state.traceManagementReducer
    );

    const [traceQueueData, setTraceQueueData] = useState(
        state?.fromDetailPage && !state.isModified ? tqResponse.traceQueueData?.data : null
    );

    // const [searchValues, setSearchValues] = useState(
    //     state?.fromDetailPage && tqResponse?.traceQueueDetails || { offset: 0, limit: rowPerPage, transactionStatus: "T" }
    // );

    const [searchValues, setSearchValues] = useState(
        state?.fromDetailPage && tqResponse?.traceQueueDetails || { offset: 0, limit: rowPerPage, transactionStatus:  "I,M", currentFiNumber }
    );

    const errorRef = useRef();
    useEffect(() => {
        if (errorRef.current)
            errorRef.current.focus();
    }, [errorMessage]);

    const [fiMap, setFiMap] = useState(null);
    const fiReducer = useSelector( (state) => state.fiReducer );

    // Use the custom hook for focus management - WCAG 2.4.3 Focus Order compliance
    const { mainContentRef } = useMainContentFocus({
        isReady: !loading && traceQueueData !== null,
        dependencies: [traceQueueData, loading],
        delay: 100,
    });

    useEffect(() => {
        isBillerRole && setFinancialInstitutionUIData(fiReducer?.financialInstitutionList, setFiMap, null, null, user?.languageCode);
    }, [fiReducer.financialInstitutionList, user?.languageCode])

    useEffect(() => {
        if (isBillerRole && !fiReducer?.financialInstitutionList?.length > 0)
            dispatch(actions.fiActions.getFinancialInstitutionList(t));

        let isLoadResults = true;
        if (state?.fromDetailPage && tqResponse?.traceQueueDetails) { // if back from the Details page
            // restore pagination settings
            setRowPerPage(searchValues.limit);
            setPageNumber(searchValues.offset / searchValues.limit);
            setDataCount(tqResponse.traceQueueData?.count);
            isLoadResults = state.isModified;  // we need to reload the results if the Trace object was modified
        }

        //Initialize searchValues with 'constant' properties, i.e. those dependent on the current user
        if (isBillerRole)
            searchValues.billerId = user.billerNonVolatileDto?.biller_id;

        if (state.fromDetailPage)
            state.fromDetailPage = null;  // resetting the coming-from-detail-page flag
        if (isLoadResults) {
            loadResults("from useEffect[]");
        }
    }, []);

    useEffect(() => {
        if (loading === false && tqResponse?.action === "GET_TRACE_QUEUE_SUCCESS") {
            const responseData = tqResponse.traceQueueData?.data;
            if (responseData?.length == 0) {
                setTraceQueueData([]);
            } else if (responseData?.length) {
                for(let i=0; i < responseData.length; i++) {
                    responseData[i].seqNo = i + 1 + searchValues.offset;
                    responseData[i].id = responseData[i].trace_id;
                }
                setTraceQueueData(responseData);
            }
            setDataCount(tqResponse.traceQueueData?.count || 0);
        }
    }, [loading]);

    const paginate = (values) => {
        searchValues.offset = values.startOffset;
        if (searchValues.limit !== (values.endOffset - values.startOffset))
            searchValues.limit = values.endOffset - values.startOffset;

        loadResults("paginate");
    };

    let loadResults = (tempText = "") => {
        if (errorMessage)
            setErrorMessage(null);
        setLoading(true);

        dispatch(
            actions.traceManagementActions.searchTraceQueue(
                searchValues,
                { ...props, history, setLoading, setErrorMessage }
            )
        );
    };

    
    return (
        <div role="main" ref={mainContentRef}>

            <ResponsiveBox sx={{ ml: 3 }}>
                <ErrorMessage 
                  error={errorMessage}
                  errorRef={errorRef}
                  mt={2}
                />
            
                <ResponsiveBox className="manageUsers__page__title">
                    {/* For Title update */}
                    <CommonContainerBox title={t("erps.tracequeue.page.title")} />
                    <Typography variant="h1">{t("erps.tracequeue.page.title")}</Typography>
                </ResponsiveBox>
 
                {traceQueueData !== null && traceQueueData !== undefined && Array.isArray(traceQueueData) && traceQueueData.length > 0 ? (
                        <>
                            <ResponsiveBox className="manageUsers_table_visible">
                                <ResponsiveDataGrid
                                    data={{
                                        rows: traceQueueData,
                                        columns: transactionSearchHeading("TQ", isBillerRole ? fiMap : null, "/trace-queue/trace-queue-detail", null, user),
                                        count: dataCount,
                                    }}
                                    onAnyPaginationAction={paginate}
                                    sumOfColumnWidth="true"
                                    pageNumber={pageNumber}
                                    setPage={setPageNumber}
                                    rowPerPage={rowPerPage}
                                    tableLabelText={"_Page Title_"}
                                    hideFooterPagination={false}
                                    pageName={"trace-management-tq"}
                                />
                            </ResponsiveBox>
                            <ResponsiveBox className="manageUsers_card_visible">
                                <CardList
                                    data={{
                                        rows: traceQueueData,
                                        columns: transactionSearchHeading("TQ", isBillerRole ? fiMap : null, "/trace-queue/trace-queue-detail", null, user),
                                        count: dataCount,
                                    }}
                                    hideFooterPagination={false}
                                    pageName={"trace-queue"}
                                    pageNumber={pageNumber}
                                    setPage={setPageNumber}
                                    rowPerPage={rowPerPage}
                                />
                            </ResponsiveBox>
                        </>
                    )
                    : (
                        <ResponsiveBox mt={1}>
                            <Typography variant="body1">
                                { (traceQueueData?.length === 0)
                                    ? (`0 ${t("erps.transactionEnquiry.page.resultfound")}`)
                                    : (t("erps.availabledocuments.searchuser.notstarted", "Search not started"))
                                }
                            </Typography>
                        </ResponsiveBox>
                    )
                }
            </ResponsiveBox>
        </div>
    );
};

export default withSidebar(TraceQueue);
